import {
    Cart,
    useGetCartLazyQuery,
    useGetConsultantProfileQuery,
    useGetFavouritesQuery,
    useGetUserQuery,
} from 'generated/graphql';
import MarketHelper from 'helpers/MarketHelper';
import { useSession } from 'next-auth/react';
import { useTranslation } from 'next-i18next';
import { OptionsObject, useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import useUser from 'stores/useCustomer';
import useMarket from 'stores/useMarket';
import { formatDateStringToIso } from 'utils/formatDateOfBirthStringToIso';
import { useOrganisationPersonalWebshop } from 'hooks/useOrganisationPersonalWebshop';
import useConsultantWebshop from 'stores/useConsultantWebshop';
import useLayout from 'stores/useLayout';
import { UserHelper } from '../helpers/UserHelper';
import { UserTypesWithGuestEnum } from '../types/UserTypesWithGuestEnum';

function RefreshState() {
    const { cart, cartId, setCartId, setCart, setUser, useLoggedIn } = useUser();
    const isLoggedIn = useLoggedIn();
    const { menu, footer, menuIsFiltred, setLayoutState, setShouldFilterMenu } = useLayout();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { organisationPersonalWebshopSlug } = useOrganisationPersonalWebshop();
    const setConsultantWebshopState = useConsultantWebshop((state) => state.setConsultantWebshopState);
    const { data: consultantWebshopData } = useGetConsultantProfileQuery({
        variables: { slug: organisationPersonalWebshopSlug },
        skip: !organisationPersonalWebshopSlug,
    });

    useEffect(() => {
        if (consultantWebshopData?.organisationPublicContent) {
            const organisation = consultantWebshopData.organisationPublicContent;
            setConsultantWebshopState({
                ...organisation,
                fullName: [organisation.first_name, organisation.middle_name, organisation.last_name]
                    .filter(Boolean)
                    .join(' '),
            });
        }
    }, [consultantWebshopData, consultantWebshopData?.organisationPublicContent, setConsultantWebshopState]);

    const setFavourites = useUser((state) => state.setFavorites);
    const {
        market,
        isMarketInitiated,
    } = useMarket((state) => ({ market: state.market, isMarketInitiated: state.isMarketInitiated }));
    const session = useSession();

    useEffect(() => {
        useUser.persist.rehydrate();
    }, []);

    const skip = session?.data?.role === 'guest' || session?.data?.role === undefined;

    const { data: favouritesData } = useGetFavouritesQuery({ skip });
    useEffect(() => {
        if (favouritesData?.favourites) {
            setFavourites(favouritesData.favourites.items.map((favourite) => favourite.id));
        }
    }, [favouritesData, setFavourites]);

    const { data: userData, loading: loadingUser } = useGetUserQuery({ skip });

    useEffect(() => {
        if (userData?.user) {
            const user_type = UserHelper.parseUserTypesWithGuest(session?.data?.role?.toLocaleLowerCase());

            setUser({
                ...userData.user,
                market: MarketHelper.localeToMarket(userData.user.locale),
                date_of_birth: formatDateStringToIso(userData.user.date_of_birth),
                user_type,
            });
        }
    }, [userData, session, setUser]);

    useEffect(() => {
        const user = session.data;
        if (userData) {
            return;
        }
        setUser({
            user_type: UserTypesWithGuestEnum.Guest,
            // Standard date of birth for signup
            date_of_birth: '2000-01-01',
            email: user?.email ?? '',
            id: 0,
            market: MarketHelper.localeToMarket(market),
            locale: market,
            login_provider: user?.provider ?? 'credentials',
            name: user?.name ?? '',
            newsletter: false,
            fullName: '',
            created_at: '',
            updated_at: '',
        });
    }, [market, userData, session, setUser]);

    const [loadCart, { data: cartData }] = useGetCartLazyQuery({
        variables: { market },
        onCompleted: (data) => {
            cartDataChanged({ newCart: data.cart.cart, oldCart: cart?.cart });
        },
    });

    useEffect(() => {
        if (cartData?.cart) {
            setCart(cartData.cart);
            setCartId(cartData.cart.cart.id);
        }
    }, [cartData, setCart, setCartId]);

    useEffect(() => {
        if (isMarketInitiated && !loadingUser && useUser.persist.hasHydrated()) {
            loadCart();
        }
    }, [userData, cartId, isMarketInitiated, loadingUser, loadCart]);

    function cartDataChanged({ oldCart, newCart } : { oldCart?: Cart; newCart: Cart }) {
        const options: OptionsObject = {
            variant: 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            autoHideDuration: 2000,
        };

        if (!oldCart) {
            return;
        }
        const newItem = newCart.items.find(
            (i) => oldCart.items.every(
                (item) => i.product_id !== item.product_id,
            ),
        );
        if (newItem) {
            enqueueSnackbar(
                t('shopping-cart.actions.save-to-cart-success', { productName: newItem?.product.name }),
                options,
            );

            return;
        }

        const deletedItem = oldCart.items.find(
            (i) => !newCart.items.some(
                (item) => i.product_id === item.product_id,
            ),
        );
        if (deletedItem) {
            enqueueSnackbar(
                t('shopping-cart.actions.delete-from-cart-success', { productName: deletedItem.product.name }), options,
            );

            return;
        }
        const changedProduct = newCart.items.find(
            (item) => oldCart.items.some(
                (i) => i.product_id === item.product_id
                    && i.quantity !== item.quantity,
            ),
        );

        const changedQuantity = changedProduct?.quantity;
        const foundItem = oldCart.items.find((i) => i.product_id === changedProduct?.product_id);
        const oldQuantity = foundItem?.quantity;

        if (changedQuantity !== undefined && oldQuantity !== undefined && changedQuantity < oldQuantity) {
            enqueueSnackbar(
                t('shopping-cart.actions.delete-from-cart-success', { productName: changedProduct?.product.name }),
                options,
            );
        } else if (changedQuantity !== undefined && oldQuantity !== undefined && changedQuantity > oldQuantity) {
            enqueueSnackbar(
                t('shopping-cart.actions.save-to-cart-success', { productName: changedProduct?.product.name }), options,
            );
        }
    }

    useEffect(() => {
        if (!menu || menuIsFiltred) return;

        if (isLoggedIn) {
            const filteredMenu = menu.filter((item) => item.slug !== '/findconsultant');
            setLayoutState(filteredMenu, footer);
            setShouldFilterMenu(true);
        }
    }, [menu, isLoggedIn, footer, menuIsFiltred, setLayoutState, setShouldFilterMenu]);

    return null;
}

export default RefreshState;
